import classNames from 'styles/utils/classNames';

interface ChipOption<T extends string> {
  label: string;
  value: T;
  /** Colored icon shown inside the chip (greyed when unselected). */
  iconSrc?: string;
}

interface Props<T extends string> {
  options: ChipOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Extra classes per chip — e.g. a max-width so a wrapped chip doesn't fill the row. */
  chipClassName?: string;
  /** Figma "Filter elements": the selected chip is a solid dark fill instead of white + ring. */
  selectedTone?: 'light' | 'dark';
  /** Chip height from `lg` up — 48px for `lg`, 40px for `md`. Mobile is always 32px. */
  size?: 'md' | 'lg';
}

export default function ChipGroup<T extends string>({
  options,
  value,
  onChange,
  chipClassName,
  selectedTone = 'light',
  size = 'md',
}: Props<T>) {
  const isLarge = size === 'lg';

  return (
    <div className="flex flex-wrap gap-ds-lg">
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={classNames(
              'typography-product-button-label-small flex h-8 min-w-[6rem] flex-1 items-center justify-center gap-ds-xs rounded-md px-ds-md transition-colors',
              isLarge ? 'lg:h-12 lg:rounded-lg' : 'lg:h-10',
              chipClassName,
              !isSelected &&
                'bg-color-bg-lightmode-secondary text-color-text-lightmode-secondary dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-secondary',
              isSelected &&
                selectedTone === 'light' &&
                'bg-color-bg-lightmode-primary text-color-text-lightmode-primary shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06),0px_1px_3px_0px_rgba(0,0,0,0.1)] ring-1 ring-color-border-input-lightmode dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-primary lg:shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)] lg:ring-color-border-selected-lightmode dark:lg:ring-color-border-selected-darkmode',
              isSelected &&
                selectedTone === 'dark' &&
                'bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert',
            )}
          >
            {option.iconSrc && (
              <img
                src={option.iconSrc}
                alt=""
                className={classNames(
                  'h-5 w-5 shrink-0 object-contain',
                  isLarge && 'lg:h-6 lg:w-6',
                  !isSelected && 'opacity-60 grayscale',
                )}
              />
            )}
            <span className="whitespace-nowrap">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
